import { useLazyQuery, useMutation } from '@apollo/client';
import { Form, Tooltip } from 'antd';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { AppContext } from '../../../../../AppContext';
import { ReactComponent as EditIcon } from '../../../../../assets/svg/pen-to-square-solid.svg';
import CommonTable from '../../../../../components/CommonTable';
import SearchComponent from '../../../../../components/SearchComponent';
import {
  BREAKPOINTS,
  DATE_FORMATS,
  DYNAMIC_CONFIG_TYPES,
  LIMIT,
  PERMISSIONS_KEY,
  PERMISSION_TYPE,
  SORT,
} from '../../../../../common/constants';
import { formatDate, hasPermission, parseDescription, formatDescriptionKey } from '../../../../../common/utils';
import { DYNAMIC_CONFIG_ITEMS } from '../../graphql/Queries';
// keep for future use
// import { CREATE_DYNAMIC_CONFIG_ITEM } from '../../graphql/Mutations';
import { UPDATE_DYNAMIC_CONFIG_ITEM } from '../../graphql/Mutations';
import ConfigToyModal from './ConfigToyModal';

const initialPaginationValue = {
  total: 0,
  current: 1,
  pageSize: 10,
};

const initialSort = {
  field: 'createdAt',
  order: 'DESC',
};

const ConfigToyTable = ({ configSubtype }) => {
  const {
    state: { permissions },
  } = useContext(AppContext);

  const [form] = Form.useForm();

  const [itemsList, setItemsList] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortedInfo, setSortedInfo] = useState({});
  const [paginationProp, setPaginationProp] = useState(initialPaginationValue);
  const [isLoading, setIsLoading] = useState(true);
  const [isActionLoading, setIsActionLoading] = useState(false);

  // keep for future use
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);

  // keep for future use
  // const [createConfig] = useMutation(CREATE_DYNAMIC_CONFIG_ITEM, {
  //   onError: () => {},
  // });

  const [updateConfig] = useMutation(UPDATE_DYNAMIC_CONFIG_ITEM, {
    onError: () => {},
  });

  const hasUpdatePermission = hasPermission(
    PERMISSIONS_KEY?.CONFIGS,
    PERMISSION_TYPE?.UPDATE,
    permissions,
  );

  // keep for future use
  // const hasArchivePermission = hasPermission(
  //   PERMISSIONS_KEY?.CONFIGS,
  //   PERMISSION_TYPE?.DELETE,
  //   permissions,
  // );

  const [fetchDynamicConfigItems] = useLazyQuery(DYNAMIC_CONFIG_ITEMS, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      const rows = data?.dynamicConfigItems?.data;
      setItemsList(Array.isArray(rows) ? rows : []);
      const total = data?.dynamicConfigItems?.count ?? 0;
      setPaginationProp((prev) => ({ ...prev, total, current: prev?.current }));
      setIsLoading(false);
    },
    onError: () => {
      setIsLoading(false);
      setItemsList([]);
    },
  });

  const buildSort = (sorter) =>
    sorter?.column
      ? {
          field: sorter?.field,
          order: sorter?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
        }
      : initialSort;

  const fetchConfigs = ({
    skip = (paginationProp?.current - 1) * (paginationProp?.pageSize || 0),
    limit = paginationProp?.pageSize || LIMIT,
    search = searchTerm,
    sorter = sortedInfo,
  } = {}) => {
    setIsLoading(true);

    const trimSearch = search?.trim();

    fetchDynamicConfigItems({
      variables: {
        filter: {
          configType: DYNAMIC_CONFIG_TYPES?.TOYS?.value,
          configSubtypes: configSubtype ? [configSubtype] : [],
          skip,
          limit,
          search: trimSearch || null,
          isActive: true,
        },
        sort: buildSort(sorter),
      },
    });
  };

  useEffect(() => {
    setPaginationProp((prev) => ({ ...prev, current: 1 }));
    fetchConfigs({
      skip: 0,
      limit: paginationProp?.pageSize || LIMIT,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configSubtype]);

  const handleTableChange = (pagination, _tableFilter, sorter) => {
    const { current } = pagination;
    const skip = (current - 1) * (pagination?.pageSize || 0);

    setPaginationProp((prev) => ({
      ...prev,
      ...pagination,
      current,
      pageSize: pagination?.pageSize || prev?.pageSize,
    }));
    setSortedInfo(sorter);

    fetchConfigs({
      skip,
      limit: pagination?.pageSize || LIMIT,
      sorter,
    });
  };

  const handleSearch = (value) => {
    const trimValue = value?.trim();
    setSearchTerm(trimValue);
    setPaginationProp((prev) => ({ ...prev, current: 1, skip: 0 }));
    fetchConfigs({ skip: 0, search: trimValue });
  };

  const handleSubmit = async (values) => {
    if (!currentItem?.id) return;
    try {
      setIsActionLoading(true);

      const response = await updateConfig({
        variables: {
          where: {
            id: currentItem?.id,
            configSubtype: currentItem?.configSubtype,
            configType: currentItem?.configType,
          },
          data: {
            description: values?.description?.trim(),
            itemKey: values?.name,
            value: values?.buttonLabel,
            isActive: currentItem?.isActive,
          },
        },
      });
      if (!response?.data?.updateDynamicConfigItem?.data?.id) {
        throw new Error(
          response?.data?.updateDynamicConfigItem?.message ||
            'Unable to update item. Please try again.',
        );
      }

      setIsModalOpen(false);
      setCurrentItem(null);
      setIsActionLoading(false);
      setPaginationProp((prev) => ({ ...prev, current: 1 }));
      fetchConfigs({ skip: 0, limit: paginationProp?.pageSize || LIMIT });
    } catch (e) {
      setIsActionLoading(false);
    }
  };

  // keep for future use
  // const handleArchive = async () => { ... };

  // Parse each record's description once and cache by id
  const parsedDescriptionMap = useMemo(() => {
    const map = {};
    itemsList.forEach((item) => {
      if (item?.id) map[item.id] = parseDescription(item.description);
    });
    return map;
  }, [itemsList]);

  // Collect all unique description keys across every row in the current page
  const allDescriptionKeys = useMemo(() => {
    const keyOrder = [];
    const keyTypes = {};

    Object.values(parsedDescriptionMap).forEach((parsed) => {
      if (!parsed) return;
      Object.entries(parsed).forEach(([k, v]) => {
        if (!(k in keyTypes)) {
          keyOrder.push(k);
          keyTypes[k] = typeof v;
        }
      });
    });

    return keyOrder.map((key) => ({ key, type: keyTypes[key] }));
  }, [parsedDescriptionMap]);

  // Build dynamic columns — reuse the already-parsed map, no re-parsing per cell
  const descriptionColumns = useMemo(
    () =>
      allDescriptionKeys.map(({ key, type }) => ({
        title: formatDescriptionKey(key),
        key: `desc_${key}`,
        width: type === 'boolean' || type === 'number' ? 100 : 150,
        align: 'left',
        ellipsis: true,
        render: (_, record) => {
          const parsed = parsedDescriptionMap[record.id];
          if (!parsed) return '-';
          const val = parsed[key];
          if (val === undefined || val === null) return '-';
          if (typeof val === 'boolean') return val ? 'Yes' : 'No';
          return String(val);
        },
      })),
    [allDescriptionKeys, parsedDescriptionMap],
  );

  const columns = useMemo(
    () => [
      {
        title: 'TYPE',
        dataIndex: 'itemKey',
        key: 'itemKey',
        ellipsis: true,
        width: 220,
        align: 'left',
        sorter: true,
        render: (_, record) => record?.itemKey || '-',
      },
      {
        title: 'BUTTON LABEL',
        dataIndex: 'value',
        key: 'value',
        ellipsis: true,
        width: 180,
        align: 'left',
        render: (_, record) => record?.value || '-',
      },
      ...descriptionColumns,
      {
        title: 'DESCRIPTION',
        dataIndex: 'description',
        key: 'description',
        width: 220,
        align: 'left',
        ellipsis: true,
        className: 'table-tooltip-cell',
        render: (_, record) => (
          <Tooltip
            title={record?.description || 'No Description'}
            placement="topLeft"
            trigger="click"
            className="pointer"
          >
            {record?.description || '-'}
          </Tooltip>
        ),
      },
      {
        title: 'CREATED AT',
        dataIndex: 'createdAt',
        key: 'createdAt',
        width: 160,
        align: 'left',
        ellipsis: true,
        sorter: true,
        render: (_, record) =>
          formatDate(record?.createdAt, DATE_FORMATS?.REGULAR) || '-',
      },
      {
        title: 'ACTIONS',
        dataIndex: 'actions',
        key: 'actions',
        width: 100,
        align: 'left',
        // eslint-disable-next-line no-undef
        fixed: window.innerWidth > BREAKPOINTS.desktop ? 'right' : false,
        render: (_, record) => (
          <div className="action-button">
            {hasUpdatePermission && (
              <Tooltip title="Edit" placement="top" zIndex={4}>
                <EditIcon
                  className="mr-16 pointer svg-icon"
                  onClick={() => {
                    setCurrentItem(record);
                    setIsModalOpen(true);
                  }}
                />
              </Tooltip>
            )}
          </div>
        ),
      },
    ],
    [hasUpdatePermission, descriptionColumns],
  );

  const locale = {
    emptyText: isLoading ? <span /> : 'No data found',
  };

  return (
    <>
      <ConfigToyModal
        form={form}
        item={currentItem}
        loadings={isActionLoading}
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        onClose={() => setCurrentItem(null)}
        onFinish={handleSubmit}
        allDescriptionKeys={allDescriptionKeys}
      />

      <div className="d-flex justify-end mb-16 flex-wrap">
        <SearchComponent
          className="search-box"
          query={searchTerm}
          setQuery={setSearchTerm}
          getData={handleSearch}
          name="Item"
        />
      </div>

      <CommonTable
        locale={locale}
        columns={columns}
        data={itemsList || []}
        loadingData={isLoading}
        onChange={handleTableChange}
        paginationConfig={paginationProp}
        rowKey={(record) => record?.id}
        scroll={{ x: 'max-content' }}
      />
    </>
  );
};

export default ConfigToyTable;
